import {
  CircleIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Grid,
  Label,
  Loading,
  Radio,
  RadioGroup,
  ReadonlyField,
  SearchMultipleSelection,
  styled,
  Text,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';
import { type ReactNode } from 'react';

import {
  EXAM_CENTER_MEMBER_STATUS_COLOR,
  EXAM_CENTER_MEMBER_STATUS_TEXT,
  ExamCenterMemberStatus,
} from '../../constants';
import { type ExamCenterMemberDetailsData } from '../../types';

const MemberDetailGrid = styled(Grid)(({ theme }) => ({
  gridTemplate: '1fr / 160px 1fr',
  gap: theme.spacing(8),
}));

const LabeledContent = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const FullLabeledContent = styled(Flex)(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

interface ExamCenterMemberDetailsModalViewProps {
  open: boolean;
  onClose: () => void;
  examCenterMember: ExamCenterMemberDetailsData;
  avatar: ReactNode;
  isLoading: boolean;
}

export default function ExamCenterMemberDetailsModalView(
  props: Readonly<ExamCenterMemberDetailsModalViewProps>,
) {
  const { open: isOpen, onClose, examCenterMember, avatar, isLoading } = props;
  const { fullName, firstName, lastName, email, appRoles, status, createdAt, lastLoginAt } =
    examCenterMember;

  const roleOptions = appRoles.map((role) => ({
    label: role,
    value: role,
  }));

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle onClose={onClose}>รายละเอียดสมาชิก</DialogTitle>
      <Divider />
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(6) }}
      >
        {!isLoading ? (
          <>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.shape.rounded(2),
              })}
            >
              <Flex
                sx={(theme) => ({
                  p: theme.spacing(3, 4),
                  gap: theme.spacing(3),
                  alignItems: 'center',
                })}
              >
                {avatar}
                <Flex sx={{ flexDirection: 'column' }}>
                  <Text variant='body1Semibold'>{fullName}</Text>
                  <Text variant='body2' color='textSecondary'>
                    {email}
                  </Text>
                </Flex>
              </Flex>
              <Divider />
              <Flex
                sx={(theme) => ({
                  p: theme.spacing(3, 4),
                  gap: theme.spacing(4),
                  alignItems: 'center',
                })}
              >
                <FullLabeledContent>
                  <Text variant='caption' color='textSecondary'>
                    บทบาทผู้ใช้
                  </Text>
                  <Text variant='body2'>{appRoles.join(', ')}</Text>
                </FullLabeledContent>
                <Divider orientation='vertical' flexItem />
                <FullLabeledContent>
                  <Text variant='caption' color='textSecondary'>
                    สถานะผู้ใช้
                  </Text>
                  <Label
                    startIcon={<CircleIcon sx={{ fontSize: '8px' }} />}
                    color={EXAM_CENTER_MEMBER_STATUS_COLOR[status]}
                  >
                    {EXAM_CENTER_MEMBER_STATUS_TEXT[status]}
                  </Label>
                </FullLabeledContent>
              </Flex>
            </Flex>

            <Divider />

            <MemberDetailGrid>
              <Text variant='body2Semibold'>ข้อมูลส่วนตัว</Text>
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
                <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
                  <Flex sx={{ flex: 1 }}>
                    <ReadonlyField label='ชื่อภาษาอังกฤษ' content={firstName} />
                  </Flex>
                  <Flex sx={{ flex: 1 }}>
                    <ReadonlyField label='นามสกุลภาษาอังกฤษ' content={lastName} />
                  </Flex>
                </Flex>
                <ReadonlyField label='อีเมลผู้ใช้' content={email} />
              </Flex>
            </MemberDetailGrid>
            <MemberDetailGrid>
              <Text variant='body2Semibold'>บทบาทและสถานะผู้ใช้</Text>
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
                <SearchMultipleSelection
                  label='บทบาทผู้ใช้ในระบบ'
                  options={[]}
                  value={roleOptions}
                  disabled
                />
                <LabeledContent>
                  <Text variant='body2Semibold'>สถานะผู้ใช้</Text>
                  <RadioGroup value={status} row sx={{ gap: (theme) => theme.spacing(4) }}>
                    <Radio label='Active' value={ExamCenterMemberStatus.ENABLED} disabled />
                    <Radio label='Inactive' value={ExamCenterMemberStatus.DISABLED} disabled />
                  </RadioGroup>
                </LabeledContent>
              </Flex>
            </MemberDetailGrid>
            <MemberDetailGrid>
              <Text variant='body2Semibold'>ข้อมูลการใช้งาน</Text>
              <Flex sx={{ gap: (theme) => theme.spacing(6) }}>
                <LabeledContent>
                  <Text variant='body2Semibold'>วันที่ลงทะเบียน</Text>
                  <Text>{formatDateTime(new Date(createdAt))}</Text>
                </LabeledContent>
                <LabeledContent>
                  <Text variant='body2Semibold'>วันที่เข้าสู่ระบบล่าสุด</Text>
                  <Text>{formatDateTime(new Date(lastLoginAt))}</Text>
                </LabeledContent>
              </Flex>
            </MemberDetailGrid>
          </>
        ) : (
          <Loading />
        )}
      </DialogContent>
    </Dialog>
  );
}
